import React, { useCallback, useEffect, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { X, Plus, MessageSquare, Inbox, AlertCircle, RefreshCw, Trash2 } from "lucide-react";
import { NAV_GROUPS, filterNavByPermissions } from "../lib/nav.js";
import { useDashboardSession } from "../auth/useDashboardSession.js";
import { useAiMode } from "../AiModeContext.js";
import { listRecentWorks, deleteAiThread, type WorkItem } from "../lib/chatApi.js";
import { formatTaipeiRelative } from "../../lib/time.js";
import { useConfirmDialog } from "./ConfirmDialog.js";

type Props = {
  variant: "desktop" | "drawer";
  open?: boolean;
  onClose?: () => void;
};

export default function Sidebar({ variant, open, onClose }: Props): React.ReactElement {
  const isDrawer = variant === "drawer";
  const session = useDashboardSession();
  const { mode } = useAiMode();

  const sidebarBody = (
    <div className="flex h-full flex-col">
      <div className="flex h-14 items-center justify-between px-4 border-b border-border">
        <NavLink to="/dashboard" className="flex items-center gap-2 group" onClick={onClose}>
          {/* Same Sentry mark as the public scan landing (ScanPage.tsx) — the
              source SVG ships as black-on-transparent, so we invert it to
              white via CSS filter to match the dark sidebar chrome. */}
          <img
            src="/sentry-icon.svg"
            alt="SENTRY"
            className="h-7 w-7"
            style={{ filter: "brightness(0) invert(1)" }}
          />
          <span className="flex flex-col leading-tight">
            <span className="text-sm font-semibold tracking-tight text-text-primary">SENTRY</span>
            <span className="text-2xs uppercase tracking-[0.18em] text-text-muted">NEXUS</span>
          </span>
        </NavLink>
        {isDrawer && (
          <button
            type="button"
            onClick={onClose}
            className="btn-ghost h-8 w-8 p-0"
            aria-label="關閉選單"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      {mode === "ai" ? (
        <WorksSidebar onNavigate={onClose} />
      ) : (
        <ProfessionalSidebar
          permissions={session.permissions}
          superAdmin={session.super_admin === true && !session.acting_tenant_id}
          onNavigate={onClose}
        />
      )}
      <div className="px-4 py-3 border-t border-border text-2xs text-text-muted">
        <div className="flex items-center justify-between">
          <span className="font-mono">SENTRY NEXUS</span>
        </div>
      </div>
    </div>
  );

  if (isDrawer) {
    return (
      <>
        {/* backdrop */}
        <div
          className={`fixed inset-0 z-40 bg-black/60 backdrop-blur-sm transition-opacity duration-200 ${
            open ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
          onClick={onClose}
          aria-hidden="true"
        />
        {/* drawer */}
        <aside
          className={`fixed inset-y-0 left-0 z-50 w-72 max-w-[85%] overflow-hidden border-r border-border bg-bg-surface transition-transform duration-200 ${
            open ? "translate-x-0" : "-translate-x-full"
          }`}
          aria-label="主選單"
        >
          {sidebarBody}
        </aside>
      </>
    );
  }

  return (
    <aside
      className="hidden lg:flex w-60 flex-shrink-0 overflow-hidden border-r border-border bg-bg-surface"
      aria-label="主選單"
    >
      {sidebarBody}
    </aside>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────────

function ProfessionalSidebar({
  permissions,
  superAdmin,
  onNavigate,
}: {
  permissions: readonly string[];
  superAdmin: boolean;
  onNavigate?: () => void;
}): React.ReactElement {
  // Permission-based filter (Custom RBAC §6). super_admin god mode bypasses
  // every gate so they see all groups. Custom-role users see groups matching
  // their assigned permission union.
  const groups = filterNavByPermissions(permissions, superAdmin, NAV_GROUPS);
  return (
    <nav className="flex-1 overflow-y-auto px-2 py-4 space-y-6">
      {groups.map((group) => (
        <div key={group.label}>
          <p className="px-3 mb-1 text-2xs font-semibold uppercase tracking-[0.14em] text-text-muted">
            {group.label}
          </p>
          <ul className="space-y-0.5">
            {group.items.map((item) => {
              const Icon = item.icon;
              // data-tour-id: stable identifier the AI guided-tour engine
              // targets when proposing "click sidebar/sites" etc. Scheme:
              //   /dashboard          → nav.overview
              //   /dashboard/sites    → nav.sites
              //   /dashboard/admin/x  → nav.admin.x
              //   /dashboard/platform/x → nav.platform.x
              const tourId = item.to === "/dashboard"
                ? "nav.overview"
                : "nav." + item.to.replace(/^\/dashboard\//, "").replace(/\//g, ".");
              return (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.to === "/dashboard"}
                    onClick={onNavigate}
                    data-tour-id={tourId}
                    className={({ isActive }) =>
                      `nav-link ${isActive ? "nav-link-active" : ""}`
                    }
                  >
                    <Icon className="h-4 w-4 flex-shrink-0" strokeWidth={2} />
                    <span className="flex-1 truncate">{item.label}</span>
                    {item.badge === "live" && (
                      <span className="flex items-center gap-1 text-2xs">
                        <span className="relative flex h-1.5 w-1.5">
                          <span className="absolute inset-0 animate-ping rounded-full bg-success/60" />
                          <span className="relative inline-flex h-full w-full rounded-full bg-success" />
                        </span>
                        <span className="text-success uppercase tracking-wider">live</span>
                      </span>
                    )}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </nav>
  );
}

/** AI mode sidebar — "工作 / Works" feed.
 *  Fetches /v1/ai/works/recent + renders. Cross-layer coordination with
 *  ChatBubble (deep in the Topology tree) goes via window CustomEvents:
 *  `ai:new-work` → ChatBubble.newThread(); `ai:select-work` with
 *  `detail.id` → setActiveId(id). Same pattern as the existing
 *  `ai-console-drawer:closed` event. */
function WorksSidebar({ onNavigate }: { onNavigate?: () => void }): React.ReactElement {
  const navigate = useNavigate();
  const session = useDashboardSession();
  const isGuest = session.role === "guest";
  const [state, setState] = useState<
    | { status: "loading" }
    | { status: "ready"; items: WorkItem[] }
    | { status: "error"; message: string }
  >({ status: "loading" });

  const load = useCallback(async () => {
    // Guest = no tenant binding → /v1/ai/works/recent will 401/403 with
    // a raw API error code that's not user-friendly. Short-circuit to a
    // friendlier zh-TW message before calling. Also keeps the「新 AI
    // 工作」 button hidden so guests don't think they can start one.
    if (isGuest) {
      setState({ status: "error", message: "尚未綁定組織，無法使用 AI 工作功能。請先完成 LINE 邀請碼綁定或聯絡管理員。" });
      return;
    }
    setState({ status: "loading" });
    try {
      const r = await listRecentWorks(15);
      setState({ status: "ready", items: r.works });
    } catch (e) {
      setState({
        status: "error",
        message: e instanceof Error ? e.message : "讀取工作列表失敗",
      });
    }
  }, [isGuest]);

  useEffect(() => { void load(); }, [load]);

  // 任一端（這裡的 onDeleted、ChatBubble 的新增 / 封存按鈕）更動 thread 列表
  // 後都會 dispatch `ai:works-changed`；雙邊 listener 各自 reload 即可同步。
  useEffect(() => {
    const onChanged = () => { void load(); };
    window.addEventListener("ai:works-changed", onChanged);
    return () => window.removeEventListener("ai:works-changed", onChanged);
  }, [load]);

  // ChatBubble 廣播 activeId 變動 → 高亮目前正在看的對話列。
  const [activeWorkId, setActiveWorkId] = useState<string | null>(null);
  useEffect(() => {
    const onActive = (e: Event) => {
      const id = (e as CustomEvent<{ id?: string | null }>).detail?.id ?? null;
      setActiveWorkId(id);
    };
    window.addEventListener("ai:active-work-changed", onActive);
    return () => window.removeEventListener("ai:active-work-changed", onActive);
  }, []);

  return (
    <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-4" aria-label="工作列表">
      <div>
        <p className="px-1 mb-2 text-2xs font-semibold uppercase tracking-[0.14em] text-text-muted">
          工作
        </p>
        <button
          type="button"
          data-tour-id="works.new"
          onClick={() => {
            if (isGuest) return; // disabled state — click should be no-op
            window.dispatchEvent(new CustomEvent("ai:new-work"));
            navigate("/dashboard/topology");
            onNavigate?.();
          }}
          disabled={isGuest}
          title={isGuest ? "尚未綁定組織，無法建立 AI 工作" : undefined}
          className={`w-full flex items-center gap-2 rounded-md border border-dashed px-3 py-2 text-xs font-medium transition-colors ${
            isGuest
              ? "border-border bg-bg-inset/40 text-text-muted cursor-not-allowed"
              : "border-border hover:border-accent/40 bg-bg-base/40 hover:bg-bg-raised text-text-muted hover:text-text-primary"
          }`}
        >
          <Plus className="h-3.5 w-3.5" />
          <span className="flex-1 text-left">新 AI 工作</span>
        </button>
      </div>

      <div>
        <div className="flex items-center justify-between px-1 mb-2">
          <p className="text-2xs font-semibold uppercase tracking-[0.14em] text-text-muted">
            最近工作
          </p>
          <button
            type="button"
            onClick={() => void load()}
            aria-label="重新載入"
            className="text-text-muted hover:text-text-primary"
            disabled={state.status === "loading"}
          >
            <RefreshCw className={`h-3 w-3 ${state.status === "loading" ? "animate-spin" : ""}`} />
          </button>
        </div>
        <WorksFeed
          state={state}
          activeWorkId={activeWorkId}
          onPick={(w) => {
            window.dispatchEvent(new CustomEvent("ai:select-work", { detail: { id: w.id } }));
            navigate(`/dashboard/topology?thread=${encodeURIComponent(w.id)}`);
            onNavigate?.();
          }}
          onDeleted={() => void load()}
        />
      </div>

      <div>
        <p className="px-1 mb-2 text-2xs font-semibold uppercase tracking-[0.14em] text-text-muted">
          切換到傳統視圖
        </p>
        <p className="px-1 text-2xs text-text-muted leading-relaxed">
          需要完整功能選單？右上角切換到「專業模式」。
        </p>
      </div>
    </nav>
  );
}

function WorksFeed({
  state,
  activeWorkId,
  onPick,
  onDeleted,
}: {
  state:
    | { status: "loading" }
    | { status: "ready"; items: WorkItem[] }
    | { status: "error"; message: string };
  activeWorkId: string | null;
  onPick: (w: WorkItem) => void;
  onDeleted: () => void;
}): React.ReactElement {
  if (state.status === "loading") {
    return (
      <ul className="space-y-1 px-1">
        {[...Array(3)].map((_, i) => (
          <li key={i} className="h-12 rounded-md bg-bg-inset/60 animate-pulse" />
        ))}
      </ul>
    );
  }
  if (state.status === "error") {
    return (
      <div className="rounded-md border border-red-500/30 bg-red-500/5 px-3 py-2.5 text-2xs text-red-300 flex items-start gap-1.5" role="alert">
        <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
        <span className="leading-relaxed">{state.message}</span>
      </div>
    );
  }
  if (state.items.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-border bg-bg-inset/50 px-3 py-6 text-center">
        <Inbox className="mx-auto mb-2 h-5 w-5 text-text-muted" />
        <p className="text-2xs text-text-muted leading-relaxed">
          最近的 AI 工作會列在這裡。<br />
          點擊上方「新 AI 工作」開始第一個。
        </p>
      </div>
    );
  }
  return (
    <ul className="space-y-1">
      {state.items.map((w) => (
        <WorkFeedItem
          key={w.id}
          work={w}
          active={w.id === activeWorkId}
          onPick={onPick}
          onDeleted={onDeleted}
        />
      ))}
    </ul>
  );
}

/** Swipe-to-reveal-delete row. iOS-mail style: drag left to expose the
 *  delete button, tap delete → confirm modal → delete API + reload.
 *  Pointer-based so it works on touch + mouse (long-press + drag on
 *  desktop) without separate touchstart / mousedown handlers. */
const REVEAL_WIDTH = 72; // matches w-18 of the delete button
const SWIPE_THRESHOLD = 24; // px past which we snap-open / -close
function WorkFeedItem({
  work, active, onPick, onDeleted,
}: {
  work: WorkItem;
  active: boolean;
  onPick: (w: WorkItem) => void;
  onDeleted: () => void;
}): React.ReactElement {
  const display = work.title?.trim() || work.one_line_excerpt || "未命名工作";
  const [offset, setOffset] = useState(0);            // current visual offset (-REVEAL_WIDTH..0)
  const [revealed, setRevealed] = useState(false);     // snap state
  const [deleting, setDeleting] = useState(false);
  // Ref tracks **synchronous** drag state during pointer events. React
  // state (offset) is closure-captured at render time, so pointerUp may
  // observe a stale value — particularly on iOS Safari where pointerup
  // can fire before the last pointermove's setState has committed. Reading
  // s.lastOffset gives us the actually-released position.
  const dragStateRef = useRef<{ startX: number; startOffset: number; movedPx: number; lastOffset: number } | null>(null);
  const dlg = useConfirmDialog();

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>): void => {
    // Ignore right-click + scroll-wheel button; only primary pointer drags.
    if (e.button !== 0 && e.pointerType === "mouse") return;
    const startOffset = revealed ? -REVEAL_WIDTH : 0;
    dragStateRef.current = { startX: e.clientX, startOffset, movedPx: 0, lastOffset: startOffset };
    (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
  };
  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>): void => {
    const s = dragStateRef.current;
    if (!s) return;
    const dx = e.clientX - s.startX;
    s.movedPx = Math.max(s.movedPx, Math.abs(dx));
    const next = Math.min(0, Math.max(-REVEAL_WIDTH, s.startOffset + dx));
    s.lastOffset = next;
    setOffset(next);
  };
  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>): void => {
    const s = dragStateRef.current;
    if (!s) return;
    dragStateRef.current = null;
    try { (e.currentTarget as HTMLDivElement).releasePointerCapture(e.pointerId); } catch { /* ignore */ }
    // Use s.lastOffset (ref, synchronous) not React state offset (stale).
    const finalOffset = s.lastOffset;
    const passedThreshold = Math.abs(finalOffset - s.startOffset) > SWIPE_THRESHOLD;
    let nextRevealed: boolean;
    if (passedThreshold) {
      nextRevealed = finalOffset < s.startOffset; // dragged further left = reveal
    } else {
      nextRevealed = revealed; // didn't pass threshold → stay
    }
    setRevealed(nextRevealed);
    setOffset(nextRevealed ? -REVEAL_WIDTH : 0);
  };

  // Click handler runs ONLY when the drag didn't pass the move threshold —
  // otherwise it would fire a stale onPick after the swipe lifts.
  const handleRowClick = (): void => {
    if (revealed) {
      // First tap on a revealed row closes the swipe instead of navigating.
      setRevealed(false);
      setOffset(0);
      return;
    }
    onPick(work);
  };

  const handleDeleteClick = async (e: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
    e.stopPropagation();
    const ok = await dlg.confirm(`封存「${display}」？\n\n此對話會立即從列表消失（管理員仍可從操作紀錄還原）。`, {
      title: "封存 AI 工作",
      destructive: true,
      okLabel: "封存",
    });
    if (!ok) return;
    setDeleting(true);
    try {
      await deleteAiThread(work.id);
      onDeleted();
      window.dispatchEvent(new CustomEvent("ai:works-changed", { detail: { archivedId: work.id } }));
    } catch (err) {
      await dlg.alert(`封存失敗：${err instanceof Error ? err.message : "未知錯誤"}`);
      setDeleting(false);
      setRevealed(false);
      setOffset(0);
    }
  };

  return (
    <li className="relative overflow-hidden rounded-md">
      {/* Reveal-button under the row — visible whenever offset < 0. */}
      <button
        type="button"
        onClick={(e) => void handleDeleteClick(e)}
        disabled={deleting}
        aria-label={`封存「${display}」`}
        className="absolute inset-y-0 right-0 flex items-center justify-center bg-danger text-text-inverse text-xs font-medium disabled:opacity-60"
        style={{ width: `${REVEAL_WIDTH}px` }}
      >
        <Trash2 className="h-4 w-4" />
      </button>
      {/* Foreground row — slides over the reveal-button. Active row
          (matches ChatBubble.activeId) gets accent left-border + tint.
          NOTE: bg must be OPAQUE so the red Trash2 button behind it
          doesn't bleed through at offset=0. We layer the accent tint
          as a separate pointer-events-none overlay instead of using
          bg-accent/10 directly on the row (which is translucent and
          let the red show through, looking like "always revealed"). */}
      <div
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onClick={handleRowClick}
        aria-current={active ? "true" : undefined}
        // overflow-hidden 雖然 <li> 已經有，但 Safari 對「祖先 overflow:hidden
        // + 自身有 transform 的子元素」會偶爾不 clip — 直接也 clip 自己這層、
        // 保證裡面長字串的 truncate 不會擠出去蓋到主畫面。
        className={`relative bg-bg-base touch-pan-y cursor-pointer select-none border-l-2 overflow-hidden ${
          active ? "border-accent" : "border-transparent"
        }`}
        style={{
          transform: `translateX(${offset}px)`,
          transition: dragStateRef.current ? "none" : "transform 180ms ease",
        }}
      >
        {active && (
          <div className="absolute inset-0 bg-accent/10 pointer-events-none" aria-hidden="true" />
        )}
        <div className={`relative flex items-start gap-2 px-2.5 py-2 min-w-0 overflow-hidden transition-colors ${active ? "" : "hover:bg-bg-hover"}`} title={display}>
          <MessageSquare className={`h-3.5 w-3.5 mt-0.5 flex-shrink-0 ${active ? "text-accent" : "text-text-muted"}`} />
          <div className="flex-1 min-w-0">
            <p className={`text-xs truncate leading-tight ${active ? "text-text-primary font-medium" : "text-text-primary"}`}>{display}</p>
            <p className="mt-0.5 text-2xs text-text-muted">
              {work.message_count} 則訊息 · {formatTaipeiRelative(work.updated_at)}
            </p>
          </div>
        </div>
      </div>
    </li>
  );
}
